import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Recipe } from '../types/mealPlan';
import { CookView } from '../components/Recipe/CookView';
import { LoadingScreen } from '../components/Layout/LoadingScreen';
import { Text, Paper, Stack } from '@mantine/core';
import { usePageTitle } from '../hooks/usePageTitle';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { isRecipeSummary } from '../utils/recipeSummary';

export const CookViewPage = () => {
  const { userId, recipeId, slugWithId } = useParams<{ userId: string; recipeId: string; slugWithId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Check if recipe was passed via state
  // A list-item summary has no steps to cook from — fetch the full recipe
  const navRecipe = location.state?.recipe as Recipe | undefined;
  const stateRecipe = navRecipe && !isRecipeSummary(navRecipe) ? navRecipe : undefined;

  // Render on the first paint when possible: navigation state survives
  // back/forward history, and revisits can reuse what this route displayed
  // earlier in the session; the mount effect below still (re)validates.
  const routeKey = slugWithId || (userId && recipeId ? `${userId}:${recipeId}` : null);
  const cookCacheKey = routeKey ? `cook-view:${routeKey}` : null;
  const cachedCookView = getPageCache<{ recipe: Recipe; isOwner: boolean }>(cookCacheKey);
  const seededRecipe = stateRecipe ?? cachedCookView?.recipe ?? null;

  const [recipe, setRecipe] = useState<Recipe | null>(seededRecipe);
  const [loading, setLoading] = useState(!seededRecipe);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(
    stateRecipe ? user?.uid === userId : cachedCookView?.isOwner ?? true
  );
  // Cook routes aren't server-injected, so a static fallback while loading is safe.
  usePageTitle(recipe ? `Cook: ${recipe.name} | Meals You Love` : 'Cook | Meals You Love');
  const mealPlanId = location.state?.mealPlanId as string | undefined;
  const assignmentId = location.state?.assignmentId as string | undefined;

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading) {
      return;
    }

    const effectiveRecipeId = recipeId || stateRecipe?.id;
    if (stateRecipe && effectiveRecipeId && stateRecipe.id === effectiveRecipeId) {
      setRecipe(stateRecipe);
      setIsOwner(user?.uid === userId);
      setLoading(false);
      setPageCache(cookCacheKey, { recipe: stateRecipe, isOwner: user?.uid === userId });
      return;
    }

    if (!slugWithId && (!recipeId || !userId)) {
      setError('Recipe not found');
      setLoading(false);
      return;
    }

    // Fetch recipe from API if not in state
    const fetchRecipe = async () => {
      try {
        // With a cache-seeded recipe on screen this refreshes silently
        // instead of flashing the loading screen.
        if (!cachedCookView) {
          setLoading(true);
        }
        setError(null);

        // Use new public endpoint with optional auth
        const headers: HeadersInit = {};
        if (user) {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const apiUrl = slugWithId
          ? `${process.env.REACT_APP_API_URL}/api/recipe/${slugWithId}`
          : `${process.env.REACT_APP_API_URL}/api/u/${userId}/recipes/${recipeId}`;

        const response = await fetch(apiUrl, {
          headers,
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('This profile is private');
          }
          throw new Error('Recipe not found');
        }

        const data = await response.json();
        setRecipe(data.recipe);
        setIsOwner(data.isOwner ?? true);
        setPageCache(cookCacheKey, {
          recipe: data.recipe,
          isOwner: data.isOwner ?? true,
        });
      } catch (error: any) {
        console.error('Error fetching recipe:', error);
        // Keep showing a cache-seeded recipe on a background-refresh failure.
        if (!cachedCookView) {
          setError(error.message || 'Failed to load recipe');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId, userId, slugWithId, user, authLoading, stateRecipe]);

  const handleBack = () => {
    // Go back in browser history, which should take us to the recipe details page
    navigate(-1);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !recipe) {
    return (
      <Paper p="xl" radius="lg" withBorder>
        <Stack gap="md" align="center">
          <Text fw={500}>Recipe not found</Text>
          <Text c="dimmed" size="sm">
            The recipe you're looking for doesn't exist or couldn't be loaded.
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <CookView
      recipe={recipe}
      onBack={handleBack}
      mealPlanId={mealPlanId}
      assignmentId={assignmentId}
      isOwner={isOwner}
    />
  );
};